
import * as getParams from '../../assets/js/get_params.js'

const POLL_INTERVAL_MS = 400;
const POLL_MAX_ATTEMPTS = 20;
const POLL_FALLBACK_ATTEMPTS_WITH_BID_ID = 40;
const OFFER_ARRAY_KEYS = ['offers', 'checkerOffers', 'mainOffers', 'sortingOffers', 'reserveOffers'];

function sleep(ms) {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

function getOfferMergeKey(offer) {
	if (!offer) return null;

	if (offer.link) {
		return `link:${offer.link}`;
	}

	if (offer.id !== undefined && offer.id !== null && offer.id !== '') {
		return `id:${offer.id}`;
	}

	if (offer.name) {
		return `name:${offer.name}|amount:${offer.amount}|period:${offer.period}`;
	}

	return null;
}

function mergeOffers(current = [], incoming = []) {
	const currentList = Array.isArray(current) ? current : [];
	const incomingList = Array.isArray(incoming) ? incoming : [];

	// If server returned a more complete list, trust its order as canonical.
	const preferIncomingOrder = incomingList.length >= currentList.length;

	const merged = [];
	const keyedIndex = new Map();

	const upsertOffer = (offer) => {
		if (!offer) return;

		const key = getOfferMergeKey(offer);

		if (!key) {
			merged.push(offer);
			return;
		}

		if (keyedIndex.has(key)) {
			merged[keyedIndex.get(key)] = offer;
			return;
		}

		keyedIndex.set(key, merged.length);
		merged.push(offer);
	};

	if (preferIncomingOrder) {
		incomingList.forEach(upsertOffer);
		currentList.forEach(upsertOffer);
		return merged;
	}

	// Partial response: keep current order stable and only append new items.
	currentList.forEach(upsertOffer);
	incomingList.forEach(upsertOffer);

	return merged;
}

function mergeShowcase(current, incoming) {
	if (!incoming) {
		return current;
	}

	if (!current) {
		return incoming;
	}

	const merged = {
		...current,
		...incoming,
		settings: {
			...(current.settings || {}),
			...(incoming.settings || {}),
		},
	};

	OFFER_ARRAY_KEYS.forEach((key) => {
		const currentOffers = Array.isArray(current[key]) ? current[key] : [];
		const incomingOffers = Array.isArray(incoming[key]) ? incoming[key] : [];
		merged[key] = mergeOffers(currentOffers, incomingOffers);
	});

	return merged;
}

function getPendingValue(showcase) {
	return Number(
		showcase?.pending ??
		showcase?.showcase?.pending ??
		0
	);
}

function normalizeShowcasePayload(response) {
	const payload = response || {};
	const nested = payload?.showcase || {};
	const settings =
		payload.settings ||
		payload.showcaseSettings ||
		nested.settings ||
		nested.params ||
		{};
	const normalized = {
		...nested,
		...payload,
	};

	OFFER_ARRAY_KEYS.forEach((key) => {
		if (Array.isArray(payload[key])) {
			normalized[key] = payload[key];
			return;
		}

		if (Array.isArray(nested[key])) {
			normalized[key] = nested[key];
			return;
		}

		normalized[key] = [];
	});

	normalized.pending = getPendingValue(payload);
	normalized.settings = {
		...settings,
		offers_mix_mode: settings.offers_mix_mode || payload.offers_mix_mode || nested.offers_mix_mode,
		offersMixMode: settings.offersMixMode || payload.offersMixMode || nested.offersMixMode,
		sort_method: settings.sort_method || payload.sort_method || nested.sort_method,
		sortMethod: settings.sortMethod || payload.sortMethod || nested.sortMethod,
	};

	return normalized;
}

function areOffersArraysEqual(a = [], b = []) {
	if (a.length !== b.length) return false;

	for (let i = 0; i < a.length; i += 1) {
		const aKey = getOfferMergeKey(a[i]);
		const bKey = getOfferMergeKey(b[i]);

		if (aKey && bKey) {
			if (aKey !== bKey) return false;
			continue;
		}

		if (JSON.stringify(a[i] || {}) !== JSON.stringify(b[i] || {})) {
			return false;
		}
	}

	return true;
}

function hasRenderableChanges(current, next) {
	if (!current) return true;
	if (!next) return false;

	for (let i = 0; i < OFFER_ARRAY_KEYS.length; i += 1) {
		const key = OFFER_ARRAY_KEYS[i];
		const currentOffers = Array.isArray(current[key]) ? current[key] : [];
		const nextOffers = Array.isArray(next[key]) ? next[key] : [];

		if (!areOffersArraysEqual(currentOffers, nextOffers)) {
			return true;
		}
	}

	const currentActiveBucket = current.activePrimaryBucket || '';
	const nextActiveBucket = next.activePrimaryBucket || '';
	if (currentActiveBucket !== nextActiveBucket) {
		return true;
	}

	const currentSettings = current.settings || {};
	const nextSettings = next.settings || {};
	if (JSON.stringify(currentSettings) !== JSON.stringify(nextSettings)) {
		return true;
	}

	if ((current.name || '') !== (next.name || '')) return true;
	if ((current.creditRating || '') !== (next.creditRating || '')) return true;

	return false;
}

var getUrl = function () {
	let host = window.location.hostname;
	let params = '';
	if (getParams.get('utm_source')){
		let utm_source = getParams.get('utm_source');
		params += '&utm_source=' + utm_source;
	}
	if (getParams.get('credit_history')){
		let credit_history = getParams.get('credit_history');
		params += '&credit_history=' + credit_history;
	}
	if (getParams.get('bid_id')){
		let bid_id = getParams.get('bid_id');
		params += '&bid_id=' + bid_id;
	}
	if (getParams.get('wm_id')) {
		let wm_id = getParams.get('wm_id');
		params += '&wm_id=' + wm_id;
	}
	if (getParams.get('utm_medium')) {
    let utm_medium = getParams.get('utm_medium');
    params += '&utm_medium=' + utm_medium;
	}

	if (getParams.get('utm_campaign')) {
		let utm_campaign = getParams.get('utm_campaign');
		params += '&utm_campaign=' + utm_campaign;
	}

	if (getParams.get('utm_term')) {
		let utm_term = getParams.get('utm_term');
		params += '&utm_term=' + utm_term;
	}

	if (getParams.get('utm_content')) {
		let utm_content = getParams.get('utm_content');
		params += '&utm_content=' + utm_content;
	}
	if (getParams.get('phone')) {
		let phone = getParams.get('phone');
		params += '&phone=' + phone;
	}



	return process.env.VUE_APP_SHOWCASE_API + '?category=мфо&host=' + host + params;
};

var requestShowcase = function () {
	return fetch(getUrl(), {
		method: 'GET',
		cache: 'no-store',
		headers: {'Content-Type': 'application/json'}
	}).then(response => {
		return response.json();
	}).then(response => {
		return normalizeShowcasePayload(response);
	});
};

var load = function () {
	return requestShowcase();
};

var pollUntilReady = async function (initialShowcase, onUpdate) {
	let showcase = initialShowcase;
	const initialPending = getPendingValue(showcase);
	const initialCheckerCount = Array.isArray(showcase?.checkerOffers) ? showcase.checkerOffers.length : 0;
	const hasBidId = Boolean(getParams.get('bid_id'));
	const hasPhone = Boolean(getParams.get('phone'));
	const forcePollingForChecker =
		(hasBidId || hasPhone) &&
		initialPending <= 0 &&
		initialCheckerCount === 0;
	const maxAttempts = forcePollingForChecker ? POLL_FALLBACK_ATTEMPTS_WITH_BID_ID : POLL_MAX_ATTEMPTS;
	let hasSeenPositivePending = initialPending > 0;

	if (initialPending <= 0 && !forcePollingForChecker) {
		return showcase;
	}

	for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
		try {
			const nextShowcase = await requestShowcase();
			const previousShowcase = showcase;
			showcase = mergeShowcase(showcase, nextShowcase);
			const pending = getPendingValue(showcase);

			if (pending > 0) {
				hasSeenPositivePending = true;
			}

			if (typeof onUpdate === 'function' && hasRenderableChanges(previousShowcase, showcase)) {
				onUpdate(showcase);
			}

			if (!forcePollingForChecker && pending <= 0) {
				break;
			}

			const checkerCount = Array.isArray(showcase?.checkerOffers) ? showcase.checkerOffers.length : 0;

			if (forcePollingForChecker && checkerCount > initialCheckerCount) {
				break;
			}

			if (forcePollingForChecker && hasSeenPositivePending && pending <= 0) {
				break;
			}
		} catch (error) {
			// Ignore transient polling errors and keep the current state on screen.
		}

		await sleep(POLL_INTERVAL_MS);
	}

	return showcase;
};

export { load, pollUntilReady, mergeShowcase }
